// Theme — the app's ONE global palette seam.
//
// tokens.css defines the dark palette under `[data-theme="dark"]` on a root
// ancestor. This provider owns the ONE place the whole app's palette is
// applied: it stamps the palette it paints on `document.documentElement`.
//
// PER-DEVICE, OUTSIDE THE EVENT LOG. The colour theme is a device preference,
// not a domain fact (memory project_theme_outside_event_log): it is persisted
// to localStorage, never emitted as an event. This is a legitimate impure-
// shell composition boundary (a mount provider): it may hold React state and
// run the sanctioned DOM/storage effects; every consumer below it stays a
// pure view reading `useTheme()`.
//
// SYSTEM (pen board 32 "Left menu – settings and account", decided 27 Oct):
// the default, and it follows the computer's light/dark setting live
// (`prefers-color-scheme`); if the OS switches while the app is open, the
// app follows. Light and Dark are explicit. An unknown stored value reads as
// System. A failed save is not announced (a preference is not work): the
// choice still applies in this window.

import { exhaustSwitchCase } from "@bilda/utils";
import {
    createContext,
    type ReactNode,
    useContext,
    useEffect,
    useState,
    useSyncExternalStore,
} from "react";
import type { ThemePreference } from "../components/Settings/types.js";
import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

// The localStorage key the device preference round-trips through. Namespaced so
// it never collides with another app's key on a shared origin.
const STORAGE_KEY = "bilda.theme";

type Palette = "light" | "dark";

// The palette the app paints under a preference, given the computer's own.
export const paletteOf = (
    theme: ThemePreference,
    systemDark: boolean,
): Palette => {
    switch (theme) {
        case "light":
        case "dark":
            return theme;
        case "system":
            return systemDark ? "dark" : "light";
        default:
            return exhaustSwitchCase(theme);
    }
};

// Parse, don't validate: localStorage hands back `string | null` (untrusted).
// Anything unexpected (absent, corrupted, a value from a future build) is
// System, the default.
export const parsePreference = (raw: string | null): ThemePreference =>
    raw === "light" || raw === "dark" || raw === "system" ? raw : "system";

// The computer's light/dark setting, live. No matchMedia (a test DOM): light.
const DARK_QUERY = "(prefers-color-scheme: dark)";
const subscribeSystem = (onChange: () => void): (() => void) => {
    if (typeof window.matchMedia !== "function") return () => {};
    const query = window.matchMedia(DARK_QUERY);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
};
const systemDark = (): boolean =>
    typeof window.matchMedia === "function" &&
    window.matchMedia(DARK_QUERY).matches;

// The live device preference, the palette it paints, and the one setter.
export type ThemeContextValue = {
    readonly theme: ThemePreference;
    readonly palette: Palette;
    readonly setTheme: (theme: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Consume the global theme. Throws if used outside the provider — a wiring bug
// surfaced loudly at first use rather than silently no-oping (mirrors
// `useAppPorts`).
export const useTheme = (): ThemeContextValue => {
    const ctx = useContext(ThemeContext);
    if (ctx === null) {
        throw new Error(
            "Theme not provided — mount must wrap the app in ThemeProvider",
        );
    }
    return ctx;
};

// The impure boundary. Holds the preference in React state (seeded once from
// the persisted value), follows the computer's setting, and reflects the
// palette onto the document root (covering the ENTIRE app) and the
// preference into storage.
export const ThemeProvider = ({
    children,
}: {
    readonly children: ReactNode;
}) => {
    const [theme, setTheme] = useState<ThemePreference>(() =>
        parsePreference(readDeviceKey(STORAGE_KEY)),
    );
    const dark = useSyncExternalStore(subscribeSystem, systemDark, () => false);
    const palette = paletteOf(theme, dark);

    useEffect(() => {
        document.documentElement.setAttribute("data-theme", palette);
    }, [palette]);
    useEffect(() => {
        writeDeviceKey(STORAGE_KEY, theme);
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, palette, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
